import React from "react";
import { Typography, Box, Avatar, Link as MuiLink, Chip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const STATUS_CHIP_COLOR = {
    active: "success",
    archived: "default",
};

function formatStatusLabel(status) {
    if (status == null || status === "") return "";
    return String(status)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function classroomStatusFromRow(row) {
    return row?.deleted_at ? "archived" : "active";
}

function teacherFromRow(row) {
    const t = row.teacher;
    if (t && typeof t === "object") {
        const first = (t.first_name ?? "").trim();
        const last = (t.last_name ?? "").trim();
        const name = `${first} ${last}`.trim();
        const initial =
            (first[0] || last[0] || "?").toUpperCase();
        return { name, avatar: t.avatar ?? null, initial };
    }
    const first = (row.teacher_first_name || "").trim();
    const last = (row.teacher_last_name || "").trim();
    const name = `${first} ${last}`.trim();
    const initial = (first[0] || last[0] || "?").toUpperCase();
    return { name, avatar: row.teacher_avatar ?? null, initial };
}

function studentsCount(row) {
    if (row.students != null && row.students !== "") return Number(row.students);
    if (row.student_count != null) return Number(row.student_count);
    if (row.students_count != null) return Number(row.students_count);
    if (row.enrolled_students != null) return Number(row.enrolled_students);
    return "—";
}

function formatDate(value) {
    if (!value) return "—";
    return String(value).slice(0, 10);
}

export const ClassroomColumns = () => [
    {
        id: "id",
        label: "ID",
        render: (value, row) => <Typography variant="body2">{row?.id ?? "—"}</Typography>,
    },
    {
        id: "name",
        label: "Name",
        render: (value, row) => {
            const label = row.classroom_name || row.name || `Classroom #${row.id}`;
            const isDeleted = !!row.deleted_at;
            return (
                <Box sx={{ textAlign: "center" }}>
                    {isDeleted ? (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {label}
                        </Typography>
                    ) : (
                        <MuiLink
                            component={RouterLink}
                            to={`/classrooms/${row.id}`}
                            underline="hover"
                            sx={{ fontWeight: 600 }}
                        >
                            {label}
                        </MuiLink>
                    )}
                </Box>
            );
        },
    },
    {
        id: "teacher",
        label: "Teacher",
        render: (value, row) => {
            const { name, avatar, initial } = teacherFromRow(row);
            if (!name) {
                return (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                        —
                    </Typography>
                );
            }
            return (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                    {avatar ? (
                        <Avatar src={avatar} alt={name} sx={{ width: 40, height: 40 }} />
                    ) : (
                        <Avatar sx={{ width: 40, height: 40 }}>{initial}</Avatar>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {name}
                    </Typography>
                </Box>
            );
        },
    },
    {
        id: "lesson_type_name",
        label: "Lesson Type",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: "center" }}>
                {row.lesson_type_name || "—"}
            </Typography>
        ),
    },
    {
        id: "students",
        label: "Students",
        render: (value, row) => {
            const n = studentsCount(row);
            return (
                <Typography variant="body2" sx={{ textAlign: "center" }}>
                    {n === "—" ? "—" : n}
                </Typography>
            );
        },
    },
    {
        id: "status",
        label: "Status",
        render: (value, row) => {
            const key = classroomStatusFromRow(row);
            const label = formatStatusLabel(key);
            return (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Chip
                        label={label}
                        color={STATUS_CHIP_COLOR[key] || "default"}
                        size="small"
                        variant={STATUS_CHIP_COLOR[key] ? "filled" : "outlined"}
                    />
                </Box>
            );
        },
    },
    {
        id: "start_date",
        label: "Start Date",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: "center" }}>{formatDate(row.start_date)}</Typography>
        ),
    },
    {
        id: "end_date",
        label: "End Date",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: "center" }}>{formatDate(row.end_date)}</Typography>
        ),
    },
];
